import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { MobileMenu } from './MobileMenu';

const Nav = styled.nav`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 1000;
    background-color: ${({ theme }) => theme.colors.primary};
    padding: 1rem 2rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        padding: 1rem 1.5rem;
    }
`;

const LogoContainer = styled(Link)`
    display: flex;
    align-items: center;
    gap: 1rem;
    text-decoration: none;
    cursor: pointer;
    padding: 0;
`;

const Logo = styled.img`
    height: 50px;
    width: auto;

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        height: 40px;
    }
`;

const BrandText = styled.span`
    font-size: 1.5rem;
    font-weight: ${({ theme }) => theme.fontWeights.bold};
    color: ${({ theme }) => theme.colors.backgroundDark};
    white-space: nowrap;

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: 1.25rem;
    }

    @media (max-width: 480px) {
        font-size: 1rem;
    }
`;

const DesktopMenu = styled.ul`
    display: flex;
    list-style: none;
    gap: 2.5rem;
    margin: 0;
    padding: 0;

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        display: none;
    }
`;

const MenuLink = styled(Link)`
    text-decoration: none;
    color: ${({ theme }) => theme.colors.backgroundDark};
    font-weight: ${({ theme }) => theme.fontWeights.semibold};
    font-size: 1rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    transition: opacity 0.2s ease;
    cursor: pointer;
    padding: 0;

    &:hover {
        opacity: 0.7;
    }
`;

const MenuItem = styled.li``;

const HamburgerButton = styled.button`
    display: none;
    background: none;
    border: none;
    cursor: pointer;
    padding: 0.5rem;
    z-index: 1001;

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        display: block;
    }
`;

const HamburgerIcon = styled.div<{ isOpen: boolean }>`
    width: 30px;
    height: 20px;
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: space-between;

    &::before,
    &::after {
        content: '';
        width: 30px;
        height: 2px;
        background-color: ${({ theme }) => theme.colors.backgroundDark};
        transition: transform 0.3s ease;
        transform-origin: center;
    }

    ${({ isOpen }) =>
        isOpen
            ? `
        &::before {
            transform: translateY(9px) rotate(45deg);
        }
        &::after {
            transform: translateY(-9px) rotate(-45deg);
        }
    `
            : `
        &::before {
            transform: translateY(0) rotate(0);
        }
        &::after {
            transform: translateY(0) rotate(0);
        }
    `}
`;

export const Navigation: React.FC = () => {
    const location = useLocation();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    const allMenuItems = [
        { label: 'Home', path: '/' },
        { label: 'Albums', path: '/albums' },
        { label: 'Videos', path: '/videos' },
        { label: 'Gallery', path: '/gallery' },
        { label: 'Shows', path: '/shows' },
        { label: 'Story', path: '/story' },
        { label: 'Contact', path: '/contact' },
    ];

    // Desktop menu excludes Home (logo serves that purpose)
    const desktopMenuItems = allMenuItems.filter((item) => item.path !== '/');

    const handleMobileMenuClose = () => {
        setIsMobileMenuOpen(false);
    };

    // Close mobile menu on escape key
    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                setIsMobileMenuOpen(false);
            }
        };

        if (isMobileMenuOpen) {
            document.addEventListener('keydown', handleEscape);
            // Prevent body scroll when menu is open
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.body.style.overflow = 'unset';
        };
    }, [isMobileMenuOpen]);

    return (
        <>
            <Nav>
                <LogoContainer to="/">
                    <Logo src={`/images/tbb-logo.png`} alt="The Butterscotch Boys Logo" />
                    <BrandText>The Butterscotch Boys</BrandText>
                </LogoContainer>

                <DesktopMenu>
                    {desktopMenuItems.map((item) => (
                        <MenuItem key={item.path}>
                            <MenuLink to={item.path}>{item.label}</MenuLink>
                        </MenuItem>
                    ))}
                </DesktopMenu>

                <HamburgerButton
                    onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                    aria-label="Toggle menu"
                >
                    <HamburgerIcon isOpen={isMobileMenuOpen} />
                </HamburgerButton>
            </Nav>

            <MobileMenu
                isOpen={isMobileMenuOpen}
                menuItems={allMenuItems}
                onClose={handleMobileMenuClose}
                currentPath={location.pathname}
            />
        </>
    );
};
